import db from "../database";
import { SyncData } from "../components/PointOfSale/Restaurant/Tables-Management/SyncData";

const FB_ORDER_CO_FIELDS = {
  salesHistory: "Y",
  fbOrderSync: "N",
  tableSync: "N",
  statusType: "OPN",
  fbOrderStatus: "CO",
  color: "#a7c957",
};

const SPLIT_FB_ORDER_CO_FIELDS = {
  fbOrderStatus: "CO",
  salesHistory: "Y",
  tableSync: "N",
};

const TABLE_RESET_FIELDS = {
  salesHistory: "Y",
  fbOrderSync: "N",
  fbOrderStatus: "CO",
  statusType: "OPN",
  noOfPersons: 0,
  guestName: "",
  waiter: null,
  referredBy: "",
  guestType: "",
  time: null,
  color: "#a7c957",
  tableSync: "N",
  ordered: "N",
  wsData: {},
  items: [],
  lines: [],
};

const MERGED_TABLE_RESET_FIELDS = {
  statusType: "OPN",
  noOfPersons: 0,
  guestName: "",
  waiter: null,
  referredBy: "",
  guestType: "",
  time: null,
  color: "#a7c957",
  tableSync: "N",
  wsData: {},
  items: [],
  lines: [],
};

const markFbOrdersCompleted = async (fbOrders, sOrderID, coFields = FB_ORDER_CO_FIELDS) => {
  const completed = [];
  for (const fbOrder of fbOrders) {
    if (fbOrder.fbOrderStatus === "IP") {
      Object.assign(fbOrder, {
        ...coFields,
        sOrderID,
        wsData: {},
        cart: { ...fbOrder.cart, items: [] },
        lines: [],
      });
      await db.fbOrderData.put(fbOrder);
      completed.push(fbOrder);
    }
  }
  return completed;
};

// True while ANY in-progress order exists for the table (split siblings included).
// Used to guard every "set table to OPN/Available" transition: a table with a
// live unpaid order must never show as Available.
export const hasActiveOrdersForTable = async (cwrFbTableId) => {
  if (!cwrFbTableId) return false;
  const fbOrders = await db.fbOrderData.where("cwrFbTableId").equals(cwrFbTableId).toArray();
  return fbOrders.some((o) => o.fbOrderStatus === "IP");
};

export const cleanupNormalTable = async (tableRecord, tblData, sOrderID) => {
  const fbOrders = await db.fbOrderData.where("cwrFbTableId").equals(tblData.cwrFbTableId).toArray();
  // Only settle non-split orders here — split-bill siblings settle one at a
  // time via cleanupSplitTable and must not be force-completed by another
  // split's payment.
  const normalOrders = fbOrders.filter((o) => !o.splitTable);
  await markFbOrdersCompleted(normalOrders, sOrderID);

  const refreshed = await db.fbOrderData.where("cwrFbTableId").equals(tblData.cwrFbTableId).toArray();
  const remainingActive = refreshed.filter((o) => o.fbOrderStatus === "IP");
  if (remainingActive.length > 0) {
    // Another order (e.g. an unsettled split sibling) is still open — keep the
    // table visibly occupied instead of flipping it to Available with live items.
    const remainingItems = remainingActive.flatMap((o) => o.wsData?.items || o.cart?.items || []);
    Object.assign(tableRecord, { salesHistory: "Y", tableSync: "N", statusType: "ONG", color: "#bc4749", items: remainingItems });
  } else {
    Object.assign(tableRecord, { ...TABLE_RESET_FIELDS });
  }

  await db.tableData.put(tableRecord, tableRecord.cwrFbTableId);
  // Bill settled — drop this table's Prepare Bill marker so the NEXT order on
  // the same table prints its receipt normally.
  localStorage.removeItem(`isPreparedBill_${tableRecord.cwrFbTableId}`);
  SyncData(tableRecord, "upsertTableStatus");

  const currentOrder = normalOrders.find((o) => o.sOrderID === sOrderID);
  if (currentOrder) {
    await SyncData(currentOrder, "upsertFbOrder");
  }
};

export const cleanupMergedTables = async (tableRecord, sOrderID) => {
  const parentId = tableRecord.cwrFbTableId;
  const childIds = (tableRecord.merge || []).map((m) => m.fbMTableId);
  const allIds = [parentId, ...childIds];

  const fbOrders = await db.fbOrderData.where("cwrFbTableId").equals(parentId).toArray();
  await markFbOrdersCompleted(fbOrders, sOrderID);

  // Per-id update with a plain object — a .modify() closure referencing the
  // module-level MERGED_TABLE_RESET_FIELDS loses that binding over the SQLite
  // IPC bridge (function toString serialization) and would reset nothing.
  for (const id of allIds) {
    await db.tableData.update(id, { ...MERGED_TABLE_RESET_FIELDS });
  }
  localStorage.removeItem(`isPreparedBill_${parentId}`);

  await SyncData(
    {
      fbtableStatusId: tableRecord.fbtableStatusId,
      cwrFbTableId: tableRecord.cwrFbTableId,
      cwrFbsectionId: tableRecord.cwrFbsectionId,
      statusType: "OPN",
      noOfPersons: 0,
      guestName: "",
      guestType: "",
      referredBy: "",
      waiter: tableRecord.waiter || null,
      merge: tableRecord.merge,
    },
    "upsertTableStatus"
  );

  await db.tableData
    .where("cwrFbTableId")
    .anyOf(allIds)
    .modify((t) => {
      t.merge = [];
      t.isMergedParent = "N";
      t.isMergedChild = "N";
      t.parentTableId = null;
      t.mergedTableIds = [];
      t.displayName = t.name;
    });

  await SyncData(tableRecord, "upsertFbOrder");
};

export const cleanupSplitTable = async (fbOrders, sOrderID) => {
  const completed = await markFbOrdersCompleted(fbOrders, sOrderID, SPLIT_FB_ORDER_CO_FIELDS);
  for (const fbOrder of completed) {
    await SyncData(fbOrder, "upsertFbOrder");
  }
};

// BUG-1393 (confirmed root cause, Compass note 2026-08-17): in pure online
// billing mode (posConfig.disableOfflineBilling === "Y"), syncOrders()'s
// "order already exists in db.orders" shortcut branch — taken when a
// WebSocket ORDER_UPSERT races ahead of the HTTP payment response — only
// marks the order synced and dispatches a UI event. It never reaches
// processOrderApproved, so the normal table-cleanup (cleanupNormalTable /
// cleanupMergedTables / cleanupSplitTable) never runs: the table stays
// occupied/"Bill Requested" and the order stays fbOrderStatus "IP" forever,
// only correcting itself if some unrelated action happens to touch it later.
//
// This performs that same cleanup for a background-completed order, using
// ONLY the order's own stamped identity (order.tableId / order.splitTable) —
// never the currently-selected UI table — because this runs during
// background sync and the cashier may already be on a different table by
// the time it fires (per the QA note's own safety requirement). It never
// touches the live cart/UI, only local DB + server sync, which is why the
// split-table branch here is a reduced, DB-only version of the foreground
// cleanupSplitTable path in useOrderProcessing.js (no cart regeneration —
// there is no "current cart" to regenerate for an order that already moved
// on in the background).
export const cleanupTableForBackgroundSyncedOrder = async (order) => {
  if (!order?.tableId) return; // not a dine-in/restaurant order — nothing to release
  const tableRecord = await db.tableData.where("cwrFbTableId").equals(order.tableId).first();
  if (!tableRecord) return;

  const isMerged = Array.isArray(tableRecord.merge) && tableRecord.merge.length > 0;
  if (isMerged) {
    await cleanupMergedTables(tableRecord, order.sOrderID);
    return;
  }

  if (tableRecord.isSplitParent === "Y" && order.splitTable && Array.isArray(tableRecord.splitTables)) {
    const fbOrders = (await db.fbOrderData.where("cwrFbTableId").equals(order.tableId).toArray()).filter(
      (o) => o.splitTable === order.splitTable
    );
    await cleanupSplitTable(fbOrders, order.sOrderID);
    const updatedSplitTables = tableRecord.splitTables.map((st) =>
      st.splitTable === order.splitTable ? { ...st, status: "OPN", ordered: "N" } : st
    );
    await db.tableData.update(tableRecord.cwrFbTableId, { splitTables: updatedSplitTables });
    return;
  }

  await cleanupNormalTable(tableRecord, { cwrFbTableId: order.tableId }, order.sOrderID);
};
